import { useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import {
  useSupabaseClient,
  useSupabaseReady,
} from '../supabase/SupabaseProvider';

const BUCKET = 'chat-attachments';

interface SendPhotoParams {
  conversationId: string;
  file: File;
  inReplyToBroadcastId?: string;
}

interface SendMessageResponse {
  success: boolean;
  data: { message_id: string; created_at: string };
  errors?: string[];
}

function buildStoragePath(
  conversationId: string,
  suffix: string,
  fileName: string,
): string {
  const safe =
    fileName.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 80) || 'photo.jpg';
  return `${conversationId}/${suffix}/${safe}`;
}

function generateId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function useSendPhotoMessage() {
  const client = useSupabaseClient();
  const ready = useSupabaseReady();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendPhoto = useCallback(
    async ({ conversationId, file, inReplyToBroadcastId }: SendPhotoParams) => {
      if (!client || !ready) throw new Error('Supabase client not ready');

      const folder = generateId();
      const storagePath = buildStoragePath(conversationId, folder, file.name);
      setSending(true);
      setError(null);

      try {
        const { error: uploadError } = await client.storage
          .from(BUCKET)
          .upload(storagePath, file, {
            contentType: file.type || 'image/jpeg',
            upsert: false,
          });
        if (uploadError) throw uploadError;

        const metadata: Record<string, unknown> = {
          attachment_path: storagePath,
        };
        if (inReplyToBroadcastId) {
          metadata.in_reply_to_broadcast_id = inReplyToBroadcastId;
        }

        // graphy creates the messages + message_attachments rows in a
        // single transaction so realtime never delivers a bare image
        // message without its attachment row. We just hand off the
        // storage_path and metadata.
        let messageId: string;
        try {
          const res = await axiosRubyClient.post<SendMessageResponse>(
            `/api/admin/conversations/${conversationId}/messages`,
            {
              content: null,
              message_type: 'image',
              metadata,
              attachment: {
                storage_path: storagePath,
                file_name: file.name,
                content_type: file.type || 'image/jpeg',
                file_size: file.size,
              },
            },
          );
          messageId = res.data.data.message_id;
        } catch (postErr) {
          await client.storage
            .from(BUCKET)
            .remove([storagePath])
            .catch(() => undefined);
          throw postErr;
        }

        return { messageId, storagePath };
      } catch (err: unknown) {
        Sentry.captureException(err, {
          tags: { feature: 'comms', action: 'send_photo' },
          extra: { conversation_id: conversationId },
        });
        const message =
          (
            err as { response?: { data?: { errors?: string[] } } }
          )?.response?.data?.errors?.join(', ') ??
          (err as Error)?.message ??
          'Failed to send photo';
        setError(message);
        throw err;
      } finally {
        setSending(false);
      }
    },
    [client, ready],
  );

  return { sendPhoto, sending, error };
}
